import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert, Box, Button, Chip, CircularProgress, Paper, Snackbar, Tab, Tabs, Typography
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import PublishIcon from '@mui/icons-material/Publish';
import SaveIcon from '@mui/icons-material/Save';
import UnpublishedIcon from '@mui/icons-material/Unpublished';
import FanPollEditor from './FanPollEditor';
import FanPollManager from './FanPollManager';
import FanPollPreview from './FanPollPreview';
import FanPollResults from './FanPollResults';
import FanPollPublishDialog from './FanPollPublishDialog';
import FanPollQuestionIdeasDialog from './FanPollQuestionIdeasDialog';
import {
    calculateFanPollState, createEmptyFanPoll, createFanPollForSameTopic, mapFanPollResponseToDraft,
    normalizedDraftJson, toFanPollRequest, newFanPollOption, mergeFanPollTags
} from './fanPollModel';
import { hasValidationErrors, validateFanPoll } from './fanPollValidation';
import {
    generateFanPollCopy, generateFanPollQuestionIdeas, generateFanPollTags,
    getFanPoll, publishFanPoll, saveFanPoll, unpublishFanPoll
} from './fanPollApi';

const emptyMetadata = { published: false, state: 'DRAFT', createDate: null, modifyDate: null, publishDate: null };
const stateColor = (state) => ({ DRAFT: 'default', SCHEDULED: 'info', ACTIVE: 'success', CLOSED: 'warning' }[state] ?? 'default');

const metadataFromResponse = (poll) => ({
    published: Boolean(poll?.published),
    state: poll?.state ?? calculateFanPollState(poll, poll?.published),
    createDate: poll?.createDate ?? null,
    modifyDate: poll?.modifyDate ?? null,
    publishDate: poll?.publishDate ?? null,
});

const ideaTopicKey = poll => JSON.stringify([
    poll.pollType,
    poll.mainTag?.idMapping?.WIKIDATA ?? poll.mainTag?.label?.trim().toLowerCase() ?? '',
]);

export default function FanPollAdmin() {
    const initialDraft = useMemo(() => createEmptyFanPoll(), []);
    const [draft, setDraft] = useState(initialDraft);
    const [cleanSnapshot, setCleanSnapshot] = useState(normalizedDraftJson(initialDraft));
    const [metadata, setMetadata] = useState(emptyMetadata);
    const [errors, setErrors] = useState({});
    const [managerOpen, setManagerOpen] = useState(false);
    const [publishDialogOpen, setPublishDialogOpen] = useState(false);
    const [sideTab, setSideTab] = useState(0);
    const [busy, setBusy] = useState(false);
    const [generatingAI, setGeneratingAI] = useState(false);
    const [generatingTags, setGeneratingTags] = useState(false);
    const [questionIdeasOpen, setQuestionIdeasOpen] = useState(false);
    const [questionIdeasLoading, setQuestionIdeasLoading] = useState(false);
    const [questionIdeasLoadingMore, setQuestionIdeasLoadingMore] = useState(false);
    const [questionIdeas, setQuestionIdeas] = useState([]);
    const [questionIdeasTopicKey, setQuestionIdeasTopicKey] = useState(null);
    const ideasRequestId = useRef(0);
    const generationRequestId = useRef(0);
    const tagRequestId = useRef(0);
    const [questionIdeasError, setQuestionIdeasError] = useState('');
    const [resultsRefreshKey, setResultsRefreshKey] = useState(0);
    const [message, setMessage] = useState(null);

    const dirty = normalizedDraftJson(draft) !== cleanSnapshot;
    const displayedState = draft.id ? calculateFanPollState(draft, metadata.published) : 'UNSAVED';

    useEffect(() => {
        const warn = (event) => {
            if (!dirty) return;
            event.preventDefault();
            event.returnValue = '';
        };
        window.addEventListener('beforeunload', warn);
        return () => window.removeEventListener('beforeunload', warn);
    }, [dirty]);

    const applyResponse = useCallback((poll) => {
        const nextDraft = mapFanPollResponseToDraft(poll);
        setDraft(nextDraft);
        setCleanSnapshot(normalizedDraftJson(nextDraft));
        setMetadata(metadataFromResponse(poll));
        setErrors({});
        setResultsRefreshKey(value => value + 1);
    }, []);

    const confirmDiscard = () => !dirty || window.confirm('Discard the unsaved fan-poll changes?');

    const clearIdeaCache = () => {
        ideasRequestId.current += 1;
        setQuestionIdeas([]);
        setQuestionIdeasTopicKey(null);
        setQuestionIdeasError('');
        setQuestionIdeasLoading(false);
        setQuestionIdeasLoadingMore(false);
        setQuestionIdeasOpen(false);
    };

    const resetEditor = next => {
        generationRequestId.current += 1;
        tagRequestId.current += 1;
        setGeneratingAI(false);
        setGeneratingTags(false);
        setDraft(next);
        setCleanSnapshot(normalizedDraftJson(next));
        setMetadata(emptyMetadata);
        setErrors({});
        setSideTab(0);
    };

    const handleNew = () => {
        if (!confirmDiscard()) return;
        clearIdeaCache();
        resetEditor(createEmptyFanPoll());
    };

    const handleNewSameTopic = () => {
        if (!draft.mainTag || !confirmDiscard()) return;
        const next = createFanPollForSameTopic(draft);
        resetEditor(next);
        if (next.tagList.length <= 1 && next.mainTag?.idMapping?.WIKIDATA) fetchTagsForTopic(next);
        openInspire(next);
    };

    const handleLoad = async (pollId) => {
        if (!confirmDiscard()) return;
        setBusy(true);
        try {
            clearIdeaCache();
            generationRequestId.current += 1;
            tagRequestId.current += 1;
            setGeneratingAI(false);
            setGeneratingTags(false);
            applyResponse(await getFanPoll(pollId));
            setManagerOpen(false);
            setMessage({ severity: 'success', text: 'Fan poll loaded.' });
        } catch (error) {
            setMessage({ severity: 'error', text: `Load failed: ${error.message}` });
        } finally {
            setBusy(false);
        }
    };

    const handleSave = async () => {
        const nextErrors = validateFanPoll(draft);
        setErrors(nextErrors);
        if (hasValidationErrors(nextErrors)) {
            setMessage({ severity: 'error', text: 'Correct the highlighted fields before saving.' });
            return;
        }
        setBusy(true);
        try {
            applyResponse(await saveFanPoll(toFanPollRequest(draft)));
            setMessage({ severity: 'success', text: 'Fan poll saved.' });
        } catch (error) {
            setMessage({ severity: 'error', text: `Save failed: ${error.message}` });
        } finally {
            setBusy(false);
        }
    };

    const generateCopyForDraft = async sourceDraft => {
        if (!sourceDraft.mainTag?.label || !sourceDraft.subject?.trim()) {
            setMessage({ severity: 'warning', text: 'Select a topic and poll subject first.' });
            return;
        }
        const requestId = ++generationRequestId.current;
        setGeneratingAI(true);
        try {
            const generated = await generateFanPollCopy({
                pollType: sourceDraft.pollType,
                topic: sourceDraft.mainTag.label,
                subject: sourceDraft.subject.trim(),
                optionCount: sourceDraft.options.length,
            });
            if (requestId !== generationRequestId.current) return;
            const options = (generated.options ?? []).map((label, index) => ({
                ...(sourceDraft.options?.[index] ?? newFanPollOption(index)),
                label,
                displayOrder: index,
            }));
            setDraft(current => {
                if (current.id !== sourceDraft.id || ideaTopicKey(current) !== ideaTopicKey(sourceDraft)
                    || current.subject !== sourceDraft.subject || current.options.length !== sourceDraft.options.length) {
                    return current;
                }
                return {
                    ...current,
                    label: generated.label ?? current.label,
                    question: generated.question ?? current.question,
                    description: generated.description ?? current.description,
                    seoTitle: current.id ? current.seoTitle : (generated.seoTitle ?? current.seoTitle),
                    options,
                };
            });
            setMessage({ severity: 'success', text: 'AI poll text generated and validated.' });
        } catch (error) {
            if (requestId === generationRequestId.current) {
                setMessage({ severity: 'error', text: `AI generation failed: ${error.message}` });
            }
        } finally {
            if (requestId === generationRequestId.current) setGeneratingAI(false);
        }
    };

    const handleGenerateAI = () => generateCopyForDraft(draft);

    const loadQuestionIdeas = async ({ append = false, targetDraft = draft } = {}) => {
        if (!targetDraft.mainTag?.label) return;
        const key = ideaTopicKey(targetDraft);
        const requestId = ++ideasRequestId.current;
        const existingQuestions = append ? questionIdeas : [];
        setQuestionIdeasTopicKey(key);
        if (append) setQuestionIdeasLoadingMore(true);
        else setQuestionIdeasLoading(true);
        setQuestionIdeasError('');
        try {
            const result = await generateFanPollQuestionIdeas({
                pollType: targetDraft.pollType,
                topic: targetDraft.mainTag.label,
                count: append ? 20 : 25,
                existingQuestions,
            });
            if (requestId !== ideasRequestId.current) return;
            const generatedQuestions = Array.isArray(result?.questions) ? result.questions : [];
            setQuestionIdeas(current => {
                if (!append) return generatedQuestions;
                const seen = new Set(current.map(question => question.toLocaleLowerCase()));
                return [...current, ...generatedQuestions.filter(question => {
                    const normalized = question.toLocaleLowerCase();
                    if (seen.has(normalized)) return false;
                    seen.add(normalized);
                    return true;
                })];
            });
        } catch (error) {
            if (requestId !== ideasRequestId.current) return;
            if (!append) setQuestionIdeas([]);
            setQuestionIdeasError(error.message ?? String(error));
        } finally {
            if (requestId === ideasRequestId.current) {
                if (append) setQuestionIdeasLoadingMore(false);
                else setQuestionIdeasLoading(false);
            }
        }
    };

    const openInspire = (targetDraft = draft, forceFetch = false) => {
        if (!targetDraft.mainTag?.label) {
            setMessage({ severity: 'warning', text: 'Select an artist, genre, or decade first.' });
            return;
        }
        setQuestionIdeasOpen(true);
        const sameTopic = questionIdeasTopicKey === ideaTopicKey(targetDraft);
        if (forceFetch || !sameTopic || (!questionIdeas.length && !questionIdeasLoading)) {
            loadQuestionIdeas({ targetDraft });
        }
    };

    const fetchTagsForTopic = async (sourceDraft, notifyUser = false) => {
        const mainTag = sourceDraft.mainTag;
        if (!mainTag) return;
        const requestId = ++tagRequestId.current;
        const wikidataId = mainTag.idMapping?.WIKIDATA;
        setGeneratingTags(true);
        try {
            const generated = wikidataId ? await generateFanPollTags(wikidataId) : [mainTag];
            if (requestId !== tagRequestId.current) return;
            if (!Array.isArray(generated)) throw new Error('The tag response was not a list.');
            setDraft(current => {
                if (current.id !== sourceDraft.id || ideaTopicKey(current) !== ideaTopicKey(sourceDraft)) return current;
                return { ...current, tagList: mergeFanPollTags(current.mainTag, current.tagList, generated) };
            });
            if (notifyUser) setMessage({ severity: 'success', text: 'Tags generated.' });
        } catch (error) {
            if (requestId === tagRequestId.current) {
                setMessage({ severity: 'error', text: `Tag generation failed: ${error.message}` });
            }
        } finally {
            if (requestId === tagRequestId.current) setGeneratingTags(false);
        }
    };
    const handleTopicSelected = nextDraft => {
        const changedTopic = ideaTopicKey(nextDraft) !== ideaTopicKey(draft);
        if (changedTopic) clearIdeaCache();
        generationRequestId.current += 1;
        tagRequestId.current += 1;
        setGeneratingAI(false);
        setGeneratingTags(false);
        setDraft(nextDraft);
        if (!nextDraft.mainTag?.label) return;
        if (nextDraft.mainTag.idMapping?.WIKIDATA) fetchTagsForTopic(nextDraft);
        if (nextDraft.subject?.trim()) generateCopyForDraft(nextDraft);
        else openInspire(nextDraft, changedTopic);
    };

    const handleInspire = () => openInspire();

    const handleQuestionIdeaSelected = question => {
        const nextDraft = { ...draft, subject: question, question };
        setDraft(nextDraft);
        setQuestionIdeasOpen(false);
        generateCopyForDraft(nextDraft);
    };

    const handleGenerateTags = () => fetchTagsForTopic(draft, true);

    const requestPublish = () => {
        const nextErrors = validateFanPoll(draft);
        setErrors(nextErrors);
        if (hasValidationErrors(nextErrors)) {
            setMessage({ severity: 'error', text: 'Correct the highlighted fields before publishing.' });
            return;
        }
        if (!draft.id) {
            setMessage({ severity: 'warning', text: 'Save the poll before publishing.' });
            return;
        }
        if (dirty) {
            setMessage({ severity: 'warning', text: 'Save changes before publishing.' });
            return;
        }
        setPublishDialogOpen(true);
    };

    const confirmPublish = async () => {
        setBusy(true);
        try {
            const result = metadata.published ? await unpublishFanPoll(draft.id) : await publishFanPoll(draft.id);
            applyResponse(result);
            setPublishDialogOpen(false);
            setMessage({ severity: 'success', text: metadata.published ? 'Fan poll unpublished.' : 'Fan poll published.' });
        } catch (error) {
            setMessage({ severity: 'error', text: `${metadata.published ? 'Unpublish' : 'Publish'} failed: ${error.message}` });
        } finally {
            setBusy(false);
        }
    };

    return (
        <Box sx={{ width: '100%', maxWidth: 1440, mx: 'auto', p: 2, height: 'calc(100vh - 52px)', display: 'flex', flexDirection: 'column' }}>
            <Paper elevation={4} sx={{ p: 1.5, mb: 2, display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                    <Button size="small" variant="outlined" startIcon={<AddCircleOutlineIcon />} onClick={handleNew} disabled={busy}>New</Button>
                    <Button size="small" variant="outlined" startIcon={<AddCircleOutlineIcon />} onClick={handleNewSameTopic} disabled={busy || !draft.mainTag}>New for same topic</Button>
                    <Button size="small" variant="outlined" startIcon={<ManageSearchIcon />} onClick={() => setManagerOpen(true)} disabled={busy}>Load</Button>
                    <Chip size="small" label={displayedState} color={displayedState === 'UNSAVED' ? 'default' : stateColor(displayedState)} />
                    {dirty && <Chip size="small" label="Unsaved changes" color="warning" variant="outlined" />}
                    {busy && <CircularProgress size={20} />}
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button size="small" variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={busy || !dirty}>Save</Button>
                    <Button size="small" variant="contained" color={metadata.published ? 'error' : 'success'}
                        startIcon={metadata.published ? <UnpublishedIcon /> : <PublishIcon />}
                        onClick={requestPublish} disabled={busy || !draft.id || dirty}>
                        {metadata.published ? 'Unpublish' : 'Publish'}
                    </Button>
                </Box>
            </Paper>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(650px, 760px) minmax(480px, 1fr)' }, gap: 2, minHeight: 0, flex: 1 }}>
                <Paper elevation={6} sx={{ p: 2, overflowY: 'auto' }}>
                    <Typography variant="h5" sx={{ mb: 2 }}>Fan poll editor</Typography>
                    <FanPollEditor draft={draft} errors={errors} onChange={setDraft} onTopicSelected={handleTopicSelected}
                        onGenerateAI={handleGenerateAI} generatingAI={generatingAI}
                        onGenerateTags={handleGenerateTags} generatingTags={generatingTags}
                        onInspire={handleInspire} inspiring={questionIdeasLoading} />
                </Paper>
                <Paper elevation={6} sx={{ display: 'flex', flexDirection: 'column', minHeight: 500, overflow: 'hidden' }}>
                    <Tabs value={sideTab} onChange={(_, value) => setSideTab(value)}>
                        <Tab label="Preview" /><Tab label="Results" disabled={!draft.id} />
                    </Tabs>
                    <Box sx={{ p: 2, overflowY: 'auto', flex: 1 }}>
                        {sideTab === 0 && <FanPollPreview draft={draft} published={metadata.published} />}
                        {sideTab === 1 && <FanPollResults pollId={draft.id} refreshKey={resultsRefreshKey} />}
                    </Box>
                </Paper>
            </Box>

            <FanPollManager open={managerOpen} onClose={() => setManagerOpen(false)} onLoad={handleLoad} />
            <FanPollPublishDialog open={publishDialogOpen} published={metadata.published} label={draft.label}
                scheduled={!metadata.published && Boolean(draft.startDate) && Date.parse(draft.startDate) > Date.now()}
                onClose={() => setPublishDialogOpen(false)} onConfirm={confirmPublish} />
            <FanPollQuestionIdeasDialog open={questionIdeasOpen} loading={questionIdeasLoading}
                loadingMore={questionIdeasLoadingMore} error={questionIdeasError}
                questions={questionIdeas} onRefresh={() => loadQuestionIdeas()}
                onLoadMore={() => loadQuestionIdeas({ append: true })}
                onClose={() => setQuestionIdeasOpen(false)} onSelect={handleQuestionIdeaSelected} />

            <Snackbar open={Boolean(message)} autoHideDuration={6000} onClose={() => setMessage(null)}>
                {message ? <Alert severity={message.severity} onClose={() => setMessage(null)}>{message.text}</Alert> : undefined}
            </Snackbar>
        </Box>
    );
}
